// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LoadingOverlayStyle } from "@insite/mobius/LoadingOverlay";
import { PageStyle } from "@insite/mobius/Page";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { zIndexExtension } from "@mcmc/XDL/PreStyle/zIndex";
import { xBaseTheme } from "@mcmc/XDL/types";
import { Roboto, RobotoCondensed } from "@mcmc/XDL/Utilities";
import { css } from "styled-components";

const gapSizes = [
    { key: "none", value: 0 },
    { key: "xs", value: 5 },
    { key: "sm", value: 10 },
    { key: "md", value: 15 },
    { key: "lg", value: 20 },
    { key: "xl", value: 25 },
    { key: "xxl", value: 30 },
];

function generateGapRules(ruleKey: string) {
    return gapSizes.map(size => `&${size.key} { ${ruleKey}: ${size.value}px !important; }`).join(" ");
}

export default {
    css: css`
        html,
        body {
            height: auto;
            line-height: ${getProp("theme.typography.body.lineHeight")};
        }

        // for LiveChat popup
        #chat-widget-container {
            z-index: ${zIndexExtension.liveChat} !important;
        }

        main${PageStyle} {
            padding-top: 0;
            padding-bottom: 0;
            min-height: 250px;
            overflow: visible;
            body {
                background: transparent;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            min-height: 0;
                            overflow: visible;
                        `,
                        css`
                            min-height: 0;
                            overflow: visible;
                        `,
                    ],
                    "max",
                )}
        }

        select {
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .show-for-print {
            display: none;
        }
        @media print {
            ${PageStyle}, html, body {
                background-color: ${getColor("primary.contrast")};
            }

            h1 {
                font-size: 30px !important;
            }

            body {
                & {
                    font-size: 16px !important;
                }
            }

            h3,
            h6 {
                font-size: 18px !important;
            }

            ${LoadingOverlayStyle} {
                display: block !important;
            }
            .show-for-print {
                display: initial;
            }
            .hide-for-print {
                display: none;
            }
        }

        .body-text {
            font-size: 18px !important;
            line-height: 25px !important;
        }

        .pt- {
            ${generateGapRules("padding-top")}
        }

        .pb- {
            ${generateGapRules("padding-bottom")}
        }

        .mt- {
            ${generateGapRules("margin-top")}
        }

        .mb- {
            ${generateGapRules("margin-bottom")}
        }

        .mktoForm.mktoForm.mktoForm {
            font-family: ${Roboto.fontFamily};
            max-width: 100%;
            &,
            * {
                padding: 0;
            }

            input[type="text"],
            input[type="url"],
            input[type="email"],
            input[type="tel"],
            input[type="number"],
            input[type="date"],
            select.mktoField,
            textarea.mktoField {
                margin-top: 5px;
                width: 100%;
                box-sizing: border-box;
                padding: 0 10px;
                font-size: 14px;
                outline: 0;
                background: ${getColor("common.background")};
                border: 1px solid ${getColor("common.border")};
                border-radius: 0;
                padding: 0 10px !important;
                border-width: 1px;
                letter-spacing: 0.2px;
                line-height: 15px;
                height: 35px;
                width: 100% !important;
            }

            textarea.mktoField {
                height: auto;
                padding-top: 8px !important;
                padding-bottom: 5px !important;
                min-height: 60px;
            }

            input[type="text"]:focus,
            input[type="url"]:focus,
            input[type="email"]:focus,
            input[type="tel"]:focus,
            input[type="number"]:focus,
            input[type="date"]:focus,
            select.mktoField:focus,
            textarea.mktoField:focus {
                border: 1px solid ${getColor("common.border")}!important;
                border-radius: 0;
                padding: 0 10px;
                box-shadow: ${getProp("theme.shadows.1")};
            }

            .mktoOffset,
            .mktoGutter {
                display: none;
            }

            .mktoFieldWrap {
                display: flex;
                flex-direction: column;
                width: 420px;
                max-width: 100%;
            }

            .mktoFieldDescriptor {
                margin-bottom: 20px !important;
                max-width: 100%;
            }

            .mktoFieldWrap label {
                display: flex;
                line-height: 16px;
                font-size: 14px;
                font-weight: 600;
                width: 100% !important;
            }

            .mktoButton {
                font-family: ${RobotoCondensed.fontFamily};
                cursor: pointer;
                font-family: inherit;
                min-height: 40px;
                line-height: 1;
                -webkit-backface-visibility: visible;
                backface-visibility: visible;
                -webkit-transition: all 200ms ease-in-out;
                transition: all 200ms ease-in-out;
                padding: 0 25px;
                display: inline-block;
                min-height: 0;
                height: 40px;
                background: ${getColor("primary")};
                border: 2px solid ${getColor("primary")};
                color: ${getColor("primary.contrast")};

                line-height: 36px;
                white-space: nowrap;
                text-transform: uppercase;
                text-align: center;
                font-weight: bold;
            }

            .mktoButton:hover {
                background: ${getColor("secondary")};
                border-color: ${getColor("secondary")};
                color: ${getColor("primary.contrast")};
            }

            .mktoButton:focus {
                outline-offset: 1px;
                outline: ${getColor("text")} solid ${getProp("theme.focus.width", "2px")};
            }

            .mktoButtonWrap {
                margin-left: 0 !important;
            }

            .mktoError {
                position: static;
                display: block !important;
                opacity: 1 !important;
            }

            .mktoErrorArrowWrap {
                display: none;
            }

            .mktoError .mktoErrorMsg {
                display: block;
                margin-top: 5px;
                background: none !important;
                border: none !important;
                border-radius: 0;
                box-shadow: none;
                color: ${getColor("danger")};
                line-height: 14px;
                font-size: 12px;
                font-weight: normal;
                max-width: 100%;
                padding: 0;
                text-shadow: none;
                outline: none;
            }

            .mktoInvalid {
                padding: 0 10px 0 6px !important;
                border-left: 4px solid ${getColor("danger")} !important;
            }

            .mktoAsterix {
                display: none;
            }

            .mktoRequiredField .mktoAsterix {
                display: block;
                color: inherit;
                order: 1;
            }
        }

        #browser-not-support-container {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
            background-color: ${getColor("common.ui3")};
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            z-index: 2147483647;
            > div {
                color: ${getColor("text")};
                background: ${getColor("text.inverse")};
                border-radius: 5px;
                padding: 30px 20px;
                max-width: 600px;
                text-align: center;
                position: relative;
                .company-logo {
                    max-width: 270px;
                    margin-bottom: 20px;
                    display: inline-block;
                }
                .close-button {
                    position: absolute;
                    background-color: transparent;
                    padding: 5px;
                    top: 10px;
                    right: 10px;
                    border: none;
                    box-shadow: none;
                    cursor: pointer;
                    &:hover {
                        color: ${getColor("secondary")};
                    }
                }
                .message-title {
                    font-weight: bold;
                    margin-bottom: 10px;
                }
                .message-body {
                    margin-bottom: 20px;
                }
                .supported-browsers {
                    display: flex;
                    justify-content: center;
                    margin-top: 20px;
                    .supported-browser {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        margin-right: 30px;
                        margin-bottom: 10px;
                        &:last-child {
                            margin-right: 0;
                        }
                        .browser-logo {
                            position: relative;
                            width: 65px;
                            height: 65px;
                            margin-bottom: 5px;
                        }

                        img {
                            max-width: 100%;
                            max-height: 100%;
                            position: absolute;
                            top: 50%;
                            left: 50%;
                            transform: translate(-50%, -50%);
                        }
                    }
                }
            }
            a {
                color: ${getColor("primary")};
                &:hover {
                    color: ${getColor("secondary")};
                }
                margin-left: 5px;
            }
        }
    `,
} as xBaseTheme["globalStyle"];
